import React, { useEffect, useMemo, useState } from "react";
import { Skeleton, notification } from "antd";
import { useSearchParams } from "react-router-dom";
import PaginationWidget from "../../../widgets/PaginationWidget";
import GroundSearchBar from "./components/GroundSearchBar";
import GroundFilterBar from "./components/GroundFilterBar";
import GroundFiltersPanel from "./components/GroundFiltersPanel";
import GroundTourCard from "./components/GroundTourCard";
import ResultsToolbar from "./components/ResultsToolbar";
import LandToursApi from "../../../api-services/land-tours-api.service";
import LocationsApi from "../../../api-services/locations-api.service";
import useBreakpoint from "../../../utils/useBreakpoint";
import {
  categoryToTourType,
  GroundFilterState,
  groundFilterCount,
  parseGroundParams,
  toGroundQueryString,
  toLandTourParams,
} from "../../../utils/groundListingParams";
import type { SortValue } from "../../../utils/listingSearchParams";
import type { Country } from "../../../interfaces/country.interface";
import type { LandTour, LandTourFilterOptions } from "../../../interfaces/land-tour.interface";

const RESULTS_PER_PAGE = 20;

const formatRinggit = (value: number): string =>
  `RM ${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function GroundTourListing() {
  const isMobile = useBreakpoint();
  const locationsApi = useMemo(() => new LocationsApi(), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const filters = useMemo(() => parseGroundParams(searchParams), [queryString]);

  const [landTours, setLandTours] = useState<LandTour[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<LandTourFilterOptions>();
  const [countries, setCountries] = useState<Country[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Powers the search bar's destination suggestions. The Destination filter
  // chip is deferred, so nothing sends `location_ids` yet.
  useEffect(() => {
    let active = true;

    locationsApi
      .getCountries()
      .then(({ locations }) => active && setCountries(locations as Country[]))
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [locationsApi]);

  // A filter change resets to page 1; `queryString` and `page` together are the
  // full request key.
  useEffect(() => setPage(1), [queryString]);

  useEffect(() => {
    let active = true;
    setLoading(true);

    LandToursApi.getLandTours(toLandTourParams(filters, page, RESULTS_PER_PAGE))
      .then((result) => {
        if (!active) return;
        setLandTours(result.landTours);
        setTotal(result.total);
        // meta.filters is constant across pages, but re-reading it keeps the
        // modal in step if the brand's promotions change mid-session.
        setFilterOptions(result.filters);
      })
      .catch(() => {
        if (!active) return;
        setLandTours([]);
        setTotal(0);
        notification.error({ message: "An error occured while loading ground tours." });
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [queryString, page]);

  const applyFilters = (next: GroundFilterState) => {
    setPage(1);
    setSearchParams(toGroundQueryString(next));
  };

  const changePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div id="ground-listing-v2">
      <div className="v2-listing__header">
        <div className="v2-listing__header-inner">
          <GroundSearchBar
            isMobile={isMobile}
            filters={filters}
            countries={countries}
            onSearch={applyFilters}
          />
          <GroundFilterBar
            isMobile={isMobile}
            filters={filters}
            promotions={filterOptions?.promotions || []}
            filterCount={groundFilterCount(filters)}
            onOpenFilters={() => setFiltersOpen(true)}
            onChange={applyFilters}
          />
        </div>
      </div>

      <div className="v2-listing__body">
        <ResultsToolbar
          total={total}
          sort={filters.sort}
          onSortChange={(sort: SortValue) => applyFilters({ ...filters, sort })}
          showSort={!isMobile}
        />

        {loading ? (
          <div className="v2-listing__grid">
            {Array.from({ length: 8 }, (_, index) => (
              <div className="v2-offer-card v2-offer-card--ground v2-offer-card--loading" key={index}>
                <div className="v2-offer-card__media">
                  <Skeleton active />
                </div>
                <div className="v2-offer-card__body">
                  <Skeleton active paragraph={{ rows: 3 }} title={{ width: "80%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : landTours.length === 0 ? (
          <div className="v2-listing__empty">
            <p>No ground tours match your search.</p>
            <p>Try widening your travel month, tour type or promotions.</p>
          </div>
        ) : (
          <>
            <div className="v2-listing__grid">
              {landTours.map((tour) => {
                // B2C shows the highest per-person price; the struck-through
                // original is that price plus the admin-set save amount. A tour
                // with no pricing tiers has no price at all - the API sorts
                // those last - so the card omits the price line rather than
                // claiming RM 0.
                const price = tour.price;
                const hasSaving = tour.saveAmount > 0 && !!price;

                return (
                  <GroundTourCard
                    key={tour.id}
                    image={tour.imageUrl || tour.images[0]}
                    promotion={tour.primaryTag?.name}
                    tourType={categoryToTourType(tour.category)}
                    tourStart={tour.firstDayCity}
                    title={tour.title}
                    brand={tour.brand}
                    flightsIncluded={Boolean(tour.inclusion?.inc_flight)}
                    originalPrice={hasSaving ? formatRinggit(price! + tour.saveAmount) : undefined}
                    save={hasSaving ? formatRinggit(tour.saveAmount) : undefined}
                    price={price ? formatRinggit(price) : undefined}
                    minPax={tour.minBooking}
                  />
                );
              })}
            </div>

            {total > RESULTS_PER_PAGE && (
              <div className="v2-listing__pagination">
                <PaginationWidget
                  totalItems={total}
                  maxItemsPerPage={RESULTS_PER_PAGE}
                  current={page}
                  pageChange={changePage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <GroundFiltersPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        isMobile={isMobile}
        filters={filters}
        options={filterOptions}
        onApply={applyFilters}
      />
    </div>
  );
}
